import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { DollarSign, CheckCircle, Users, TrendingUp, Target, Award } from 'lucide-react';

const SalesProfessionals = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What metrics should sales professionals track for performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Track revenue generated, quota attainment percentage, deal size trends, pipeline velocity, win rates, customer retention, and new logo acquisition. Also document strategic wins, enterprise deals closed, and accounts saved from churn. Example: 'Achieved 127% of quota, generating $2.4M in new ARR with 34% win rate.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do salespeople demonstrate value beyond quota?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document process improvements, playbooks created, team mentorship, competitive intelligence gathered, customer advocacy developed, and cross-functional collaboration. Show how you've made the team better, not just your individual numbers."
        }
      },
      {
        "@type": "Question",
        "name": "What should I include if I missed quota this period?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Focus on pipeline built for future quarters, strategic accounts progressed, skills developed, and lessons learned. Be honest about challenges while showing what you're doing differently. Document activity metrics that show effort even when outcomes lagged."
        }
      },
      {
        "@type": "Question",
        "name": "How often should sales reps document their achievements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Weekly at minimum, ideally after every significant deal milestone. Track closed deals, strategic meetings, competitive wins, and customer testimonials in real-time. Use a tool like BragBox to log wins immediately after they happen."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Sales Professionals | BragBox</title>
        <meta name="description" content="Ace your sales performance review with actionable tips. Learn how to showcase quota achievement, strategic deals, and leadership beyond your numbers." />
        <meta name="keywords" content="sales performance review, sales self-evaluation, quota achievement, sales metrics, sales career growth, account executive review" />
        <link rel="canonical" href="https://bragbox.ai/tips/sales-professionals" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <DollarSign className="w-4 h-4" />
              Sales Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Sales Professionals
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Your numbers tell part of the story, but not the whole story. Learn how to showcase the strategic wins, relationship building, and team contributions that make you a top performer.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Salespeople Need More Than Numbers</h2>
            <p className="text-gray-300 mb-6">
              You closed a seven-figure enterprise deal that took 9 months of relationship building. You turned around a churning account by identifying a use case nobody had considered. You helped three teammates close their first deals by sharing your discovery process. But when review time comes, management just looks at quota attainment.
            </p>
            <p className="text-gray-300">
              Sales performance reviews often reduce complex, strategic work to a single number. The problem is that number doesn't capture the deals you saved for next quarter, the competitive intelligence you gathered, or the process improvements you pioneered. Your full value is bigger than your closed-won column.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Sales Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Go Beyond Quota Attainment</h3>
                    <p className="text-gray-300 mb-3">
                      Quota percentage is just the starting point. Add context: deal size trends, competitive wins, strategic accounts landed, and multi-year contract values.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved 134% of quota ($1.8M) including 3 enterprise deals over $200K each. Average deal size increased 45% YoY. Secured 2 three-year contracts worth $650K in committed revenue."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Pipeline Generation</h3>
                    <p className="text-gray-300 mb-3">
                      Closed revenue is lagging. Pipeline is leading. Show the future deals you've created and their expected close dates.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Built $4.2M qualified pipeline for Q2-Q3, including $1.1M in late-stage opportunities with 70%+ close probability. Self-sourced 35% of pipeline through LinkedIn outreach and referral strategy."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Highlight Competitive Wins</h3>
                    <p className="text-gray-300 mb-3">
                      Displacing a competitor or winning against a well-known rival shows sales skill and market value. Document who you beat and how.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Won 7 competitive deals against [Competitor], including displacing them in 2 existing accounts. Developed competitive battle card now used by entire team, contributing to 23% improvement in win rate against this competitor."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Track Customer Retention and Expansion</h3>
                    <p className="text-gray-300 mb-3">
                      Keeping customers and growing accounts shows relationship skills. Track renewals influenced, upsells closed, and churn prevented.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved 95% renewal rate on book of business ($2.1M ARR). Closed $340K in expansion revenue through strategic account reviews. Saved 2 at-risk accounts worth $180K combined by identifying and resolving adoption issues."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Process Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Did you create a discovery framework, demo script, or objection handling guide? Process contributions show leadership and scale your impact.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Developed qualification framework now used by SDR team, improving SQL conversion by 18%. Created enterprise demo flow adopted by 4 teammates, contributing to $890K in closed business across the team."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Showcase Customer Advocacy</h3>
                    <p className="text-gray-300 mb-3">
                      References, case studies, and testimonials are gold. Track customers who agreed to advocate, participate in marketing, or serve as references.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Developed 5 referenceable customers who participated in 12 reference calls, contributing to $420K in influenced revenue. Secured 2 case studies and 1 video testimonial used in marketing campaigns."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Track Team Mentorship</h3>
                    <p className="text-gray-300 mb-3">
                      Helping teammates succeed shows leadership potential. Document ride-alongs, deal coaching, and wins you influenced for others.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Mentored 2 new AEs through first 90 days, both achieving quota in Q2. Participated in 15 deal reviews, with coached deals closing at 40% higher rate than team average."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Document Cross-Functional Collaboration</h3>
                    <p className="text-gray-300 mb-3">
                      Working effectively with marketing, product, and customer success shows team orientation. Track feedback loops, product influence, and joint initiatives.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Provided product feedback from 8 lost deals, 3 becoming roadmap priorities. Collaborated with marketing on webinar generating 45 MQLs and $380K pipeline. Partnered with CS on 5 account rescues."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Capture Strategic Wins (Even If Small)</h3>
                    <p className="text-gray-300 mb-3">
                      Landing a logo in a new vertical, closing your first enterprise deal, or winning a strategic partner can be worth more than the revenue number suggests.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Closed first healthcare vertical customer ($45K), establishing reference for $800K+ healthcare pipeline. Won strategic partnership with [Company], opening door to co-selling opportunities worth $500K+ potential."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Track Wins in Real-Time</h3>
                    <p className="text-gray-300 mb-3">
                      Don't wait for review season to remember your wins. Log deals, strategic meetings, and competitive intelligence as they happen.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After every significant deal milestone, competitive win, or customer success moment, take 30 seconds to log it in BragBox. When review time comes, you'll have a goldmine of achievements ready to go.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Sales Professionals Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Revenue Metrics</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Quota attainment percentage</li>
                  <li>• Total revenue closed</li>
                  <li>• Average deal size</li>
                  <li>• Multi-year contract value</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Pipeline Metrics</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Pipeline generated</li>
                  <li>• Win rate percentage</li>
                  <li>• Sales cycle length</li>
                  <li>• Stage conversion rates</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Account Health</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Renewal rate</li>
                  <li>• Expansion revenue</li>
                  <li>• Churn prevented</li>
                  <li>• Customer references developed</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Strategic Value</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Competitive wins</li>
                  <li>• New vertical logos</li>
                  <li>• Enterprise deals closed</li>
                  <li>• Team contributions</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics should sales professionals track for performance reviews?</h3>
                <p className="text-gray-300">Track revenue generated, quota attainment percentage, deal size trends, pipeline velocity, win rates, customer retention, and new logo acquisition. Also document strategic wins, enterprise deals closed, and accounts saved from churn. Example: "Achieved 127% of quota, generating $2.4M in new ARR with 34% win rate."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do salespeople demonstrate value beyond quota?</h3>
                <p className="text-gray-300">Document process improvements, playbooks created, team mentorship, competitive intelligence gathered, customer advocacy developed, and cross-functional collaboration. Show how you've made the team better, not just your individual numbers.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should I include if I missed quota this period?</h3>
                <p className="text-gray-300">Focus on pipeline built for future quarters, strategic accounts progressed, skills developed, and lessons learned. Be honest about challenges while showing what you're doing differently. Document activity metrics that show effort even when outcomes lagged.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should sales reps document their achievements?</h3>
                <p className="text-gray-300">Weekly at minimum, ideally after every significant deal milestone. Track closed deals, strategic meetings, competitive wins, and customer testimonials in real-time. Use a tool like BragBox to log wins immediately after they happen.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Close More Than Deals. Close Your Best Review Yet.</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps sales professionals track wins, competitive victories, and strategic achievements throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SalesProfessionals;
